import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  FaCalendarAlt,
  FaCheckSquare,
  FaEnvelope,
  FaPhone,
  FaQuoteLeft,
  FaUser,
} from "react-icons/fa";
import { MdArrowOutward } from "react-icons/md";
import { CONTACTUS_API } from "../../hooks/Apis";

const SubPages = ({ pageData, seoData }) => {
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (honeypot) {
      console.warn("Bot detected! Submission blocked.");
      alert("Submission blocked. Please try again.");
      return;
    }

    // Validate required fields
    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !formData.subject ||
      !formData.message
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(CONTACTUS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          honeypot: honeypot,
        }),
      });

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      if (result.success) {
        alert(
          "Appointment request submitted successfully! We'll get back to you within 24 hours.",
        );
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setHoneypot("");
      } else {
        alert(result.error || "Submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      if (error.name === "TypeError") {
        alert("Network error. Please check your connection and try again.");
      } else {
        alert("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const { title, description, keywords, canonical, ogImage } =
    seoData[pageData.SeoName.seotitle];

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content=" DermaSlim Wellness" />
        <meta name="language" content="en" />
        <meta name="distribution" content="global" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <main className="lg:mx-4">
        {/* Hero Header */}
        <section className="relative bg-primary rounded-xl overflow-hidden mb-16">
          {/* Background Pattern Overlay */}
          <div className="absolute inset-0 bg-bg-pattern bg-cover bg-center opacity-30"></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              {pageData.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
              {pageData.subtitle}
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="lg:w-8/12">
              {/* Introduction Section */}
              <section className="mb-12">
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border mb-8">
                  <img
                    src={pageData.images.intro}
                    alt={pageData.title}
                    className="w-[1000px] h-[560px] max-w-full object-cover object-top mx-auto"
                  />
                </div>

                <div className="text-gray-600 leading-relaxed space-y-4 mb-8">
                  {pageData.introduction.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {pageData.stats.map((stat, index) => (
                    <div
                      key={index}
                      className="text-center p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="text-2xl font-bold text-primary mb-1">
                        {stat.value}
                      </div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Benefits Section */}
              <section className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-6">
                  Top Benefits
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pageData.benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <FaCheckSquare className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Complementary Treatments */}
              <section className="mb-12">
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border mb-6">
                  <img
                    src={pageData.images.complementary}
                    alt="Complementary Treatments"
                    className="w-[1000px] h-[560px] max-w-full object-cover object-top mx-auto"
                  />
                </div>

                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">
                  {pageData.complementaryTitle}
                </h2>

                <div className="text-gray-600 leading-relaxed space-y-4 mb-6">
                  {pageData.complementaryDescription.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>

                <Link to={pageData.bookingLink}>
                  <span className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors">
                    Book Appointment
                    <MdArrowOutward className="text-sm" />
                  </span>
                </Link>
              </section>

              {/* Concerns Section */}
              <section className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-6">
                  Concerns We Treat
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pageData.concerns.map((concern, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <FaCheckSquare className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{concern}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ Section */}
              <section className="mb-12" id="faqs">
                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {pageData.faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="border-b border-gray-200 last:border-b-0"
                    >
                      <button
                        className={`w-full flex justify-between items-center p-4 text-left hover:bg-gray-50 transition-colors ${
                          activeAccordion === index ? "bg-gray-50" : ""
                        }`}
                        onClick={() => toggleAccordion(index)}
                      >
                        <span className="font-medium text-gray-800">
                          {faq.question}
                        </span>
                        <span className="text-gray-600 font-bold">
                          {activeAccordion === index ? "−" : "+"}
                        </span>
                      </button>
                      {activeAccordion === index && (
                        <div className="px-4 pb-4 bg-gray-50">
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar - Contact Form */}
            <div className="lg:w-4/12">
              <div className="sticky top-24">
                {/* Right Side - Form & Info */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Appointment Form */}
                  <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-3 h-8 bg-secondary rounded-full"></div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        Book Your Appointment
                      </h2>
                    </div>

                    <p className="text-gray-600 mb-8 leading-relaxed">
                      Fill out the form below and we'll get back to you within
                      24 hours with a personalized consultation plan.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Honeypot field - hidden from users */}
                      <input
                        type="text"
                        name="honeypot"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        style={{ display: "none" }}
                        tabIndex="-1"
                        autoComplete="off"
                      />

                      <div className="grid grid-cols-1 gap-6">
                        {[
                          {
                            field: "name",
                            type: "text",
                            placeholder: "Your Full Name",
                            icon: <FaUser className="text-gray-400" />,
                          },
                          {
                            field: "email",
                            type: "email",
                            placeholder: "your@email.com",
                            icon: <FaEnvelope className="text-gray-400" />,
                          },
                          {
                            field: "phone",
                            type: "tel",
                            placeholder: "+91 00000 00000",
                            icon: <FaPhone className="text-gray-400" />,
                          },
                          {
                            field: "subject",
                            type: "text",
                            placeholder: "Consultation Type",
                            icon: <FaCalendarAlt className="text-gray-400" />,
                          },
                        ].map((item, idx) => (
                          <div key={idx} className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-2 capitalize">
                              {item.field}
                              <span className="text-red-500 ml-1">*</span>
                            </label>
                            <div className="relative">
                              <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                                {item.icon}
                              </div>
                              <input
                                type={item.type}
                                name={item.field}
                                placeholder={item.placeholder}
                                value={formData[item.field]}
                                onChange={handleChange}
                                className="w-full rounded-xl py-4 px-12 text-gray-900 bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                required
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Message */}
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-2">
                          Message
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <textarea
                          name="message"
                          placeholder="Tell us about your concerns, preferred appointment time, or any specific questions you have..."
                          rows="5"
                          value={formData.message}
                          onChange={handleChange}
                          className="rounded-xl py-4 px-4 text-gray-900 bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                          required
                        ></textarea>
                      </div>

                      {/* Button */}
                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-4 rounded-xl font-semibold tracking-wide shadow-lg transition-all flex items-center justify-center gap-3 group ${
                          loading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary transform hover:-translate-y-0.5"
                        }`}
                      >
                        {loading ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Sending Request...
                          </>
                        ) : (
                          <>
                            <span className="text-white">
                              Schedule Appointment
                            </span>
                            <svg
                              className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                              />
                            </svg>
                          </>
                        )}
                      </button>

                      <p className="text-center text-gray-500 text-sm pt-4">
                        We respect your privacy. Your information is secure and
                        will never be shared.
                      </p>
                    </form>
                  </div>
                </div>

                {/* Quick Stats Card */}
                <div className="bg-primary text-white rounded-xl p-6 mt-10">
                  <h3 className="text-lg font-semibold mb-4">
                    {pageData.whyChooseUs.title}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {pageData.whyChooseUs.points.map((point, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className="text-green-400">✓</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <section className="py-12 bg-gray-50 rounded-xl mb-12">
            <div className="max-w-4xl mx-auto text-center">
              <FaQuoteLeft className="text-3xl text-primary mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">
                {pageData.cta.title}
              </h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                {pageData.cta.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={pageData.cta.primaryLink}
                  className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                >
                  {pageData.cta.primaryText}
                  <MdArrowOutward className="text-sm" />
                </Link>
                <Link
                  to={pageData.cta.secondaryLink}
                  className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors"
                >
                  {pageData.cta.secondaryText}
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default SubPages;
